import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Search, Calendar } from 'lucide-react';
import Modal from 'react-bootstrap/Modal';
import styles from "../../_css/NotificationManagement.module.css";
import { getAllCompanies } from '../../requests';
import { notifyInfo } from '../helper/Toaster';

const CreateNotificationModal = ({
    show,
    onHide,
    onCreate,
    onDraft,
    createLoading,
    draftLoading,
    initialData, // NEW: Data to populate when editing
    isEditing    // NEW: Flag to check if in edit mode
}) => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [urgency, setUrgency] = useState('');
    const [audience, setAudience] = useState('');
    const [signature, setSignature] = useState('');

    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduledDate, setScheduledDate] = useState('');

    const [deliveryMethods, setDeliveryMethods] = useState({
        popup: false,
        banner: false,
        email: false
    });

    const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState(false);
    const [audienceDropdownOpen, setAudienceDropdownOpen] = useState(false);
    const [userSearch, setUserSearch] = useState("");
    const [companyList, setCompanyList] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);

    // Fetch company list for dropdown
    const fetchCompanies = async () => {
        try {
            const response = await getAllCompanies();
            if (response.success && Array.isArray(response.data)) {
                const companyList = response.data.map(
                    (c) => c.company_name || c.name
                );
                setCompanyList(["All Users", ...companyList]);
            }
        } catch (err) {
            console.error("Error fetching companies:", err);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    // --- NEW: Populate form when editing ---
    useEffect(() => {
        if (show && isEditing && initialData) {
            setTitle(initialData.title || '');
            setMessage(initialData.message || '');
            setUrgency(initialData.urgency || '');
            const audienceData = initialData.audienceType || initialData.audience || initialData.Audience || '';
            // Handle both string and array formats for backward compatibility
            if (Array.isArray(audienceData)) {
                setSelectedCompanies(audienceData);
                setAudience(audienceData.length > 0 ? audienceData.join(', ') : '');
            } else if (typeof audienceData === 'string' && audienceData.includes(',')) {
                const companies = audienceData.split(',').map(c => c.trim()).filter(c => c);
                setSelectedCompanies(companies);
                setAudience(companies.join(', '));
            } else {
                setSelectedCompanies(audienceData ? [audienceData] : []);
                setAudience(audienceData);
            }
            setSignature(initialData.signature || '');

            // Handle Schedule Date
            const scheduleTime = initialData.scheduledAt || initialData.scheduled_at;
            if (scheduleTime) {
                setIsScheduled(true);
                // Format ISO string to 'YYYY-MM-DDTHH:mm' for datetime-local input
                try {
                    const dateObj = new Date(scheduleTime);
                    // Adjust to local ISO string roughly for input value
                    const localIso = new Date(dateObj.getTime() - (dateObj.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
                    setScheduledDate(localIso);
                } catch (e) {
                    setScheduledDate('');
                }
            } else {
                setIsScheduled(false);
                setScheduledDate('');
            }

            // Handle Delivery Methods (Map generic backend response to state)
            let newMethods = { popup: false, banner: false, email: false };
            const incomingMethods = initialData.deliveryMethod;

            if (Array.isArray(incomingMethods)) {
                // If backend sends ['popup', 'email']
                incomingMethods.forEach(m => {
                    if (typeof m === 'string' && newMethods.hasOwnProperty(m.toLowerCase())) {
                        newMethods[m.toLowerCase()] = true;
                    }
                });
            } else if (typeof incomingMethods === 'string') {
                // If backend sends "popup, email"
                incomingMethods.split(',').forEach(m => {
                    const clean = m.trim().toLowerCase();
                    if (newMethods.hasOwnProperty(clean)) newMethods[clean] = true;
                });
            } else if (typeof incomingMethods === 'object' && incomingMethods !== null) {
                // If backend sends object {popup: true...}
                newMethods = { ...newMethods, ...incomingMethods };
            }
            setDeliveryMethods(newMethods);

        } else if (show && !isEditing) {
            // Reset if opening in Create mode
            resetForm();
        }
    }, [show, isEditing, initialData]);

    const resetForm = () => {
        setTitle('');
        setMessage('');
        setUrgency('');
        setAudience('');
        setSignature('');
        setIsScheduled(false);
        setScheduledDate('');
        setDeliveryMethods({
            popup: false,
            banner: false,
            email: false
        });
        setUrgencyDropdownOpen(false);
        setAudienceDropdownOpen(false);
        setUserSearch("");
        setSelectedCompanies([]);
    };

    const handleDeliveryMethodChange = (method) => {
        setDeliveryMethods(prev => ({
            ...prev,
            [method]: !prev[method]
        }));
    };

    const handleCreate = () => {
        if (isScheduled && !scheduledDate) {
            notifyInfo("Please select a date and time for the scheduled notification.");
            return;
        }

        if (selectedCompanies.length === 0) {
            notifyInfo("Please select at least one company or 'All Users'.");
            return;
        }

        const notificationData = {
            id: isEditing ? (initialData.id || initialData._id) : null, // Pass ID if editing
            title: title,
            message: message,
            urgency: urgency,
            audience: selectedCompanies, // Send as array
            signature: signature,
            deliveryMethods: deliveryMethods,
            status: isScheduled ? "Scheduled" : "Sent",
            scheduledAt: isScheduled
                ? new Date(
                    new Date(scheduledDate).getTime() -
                    new Date().getTimezoneOffset() * 60000
                ).toISOString()
                : null

        }
        onCreate(notificationData);
    }

    const handleDraft = () => {
        if (selectedCompanies.length === 0) {
            notifyInfo("Please select at least one company or 'All Users'.");
            return;
        }

        const notificationData = {
            id: isEditing ? (initialData.id || initialData._id) : null, // Pass ID if editing
            title: title,
            message: message,
            urgency: urgency,
            audience: selectedCompanies, // Send as array
            signature: signature,
            deliveryMethods: deliveryMethods,
            status: "Draft",
            scheduledAt: isScheduled
                ? new Date(
                    new Date(scheduledDate).getTime() -
                    new Date().getTimezoneOffset() * 60000
                ).toISOString()
                : null

        }
        onDraft(notificationData);
    }

    const handleCompanyToggle = (company) => {
        setSelectedCompanies(prev => {
            if (company === "All Users") {
                // If "All Users" is selected, clear all other selections
                return prev.includes("All Users") ? [] : ["All Users"];
            } else {
                // Remove "All Users" if a specific company is selected
                let newSelection = prev.filter(c => c !== "All Users");
                if (newSelection.includes(company)) {
                    newSelection = newSelection.filter(c => c !== company);
                } else {
                    newSelection.push(company);
                }
                return newSelection;
            }
        });
    }

    useEffect(() => {
        // Update audience display string when selectedCompanies changes
        if (selectedCompanies.length > 0) {
            setAudience(selectedCompanies.join(', '));
        } else {
            setAudience('');
        }
    }, [selectedCompanies]);

    const handleClose = () => {
        resetForm();
        onHide();
    }

    const getMinDateTime = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            dialogClassName={styles.modal38}
        >
            {/* Header */}
            <Modal.Header className={styles.modalHeader}>
                <Modal.Title className={styles.modalTitle}>
                    {isEditing ? "Edit Notification" : "Create Notification"}
                </Modal.Title>
                <button
                    onClick={handleClose}
                    className={styles.closeButton}
                >
                    <X size={24} color="#374151" />
                </button>
            </Modal.Header>

            {/* Body */}
            <Modal.Body className={styles.modalBody}>
                {/* Title Field */}
                <div className={styles.formField}>
                    <label className={styles.formLabel}>
                        Title
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className={styles.formInput}
                        placeholder="Enter notification title"
                    />
                </div>

                {/* Message Field */}
                <div className={styles.formField}>
                    <label className={styles.formLabel}>
                        Message
                    </label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={5}
                        className={styles.formTextarea}
                        placeholder="Enter notification message"
                    />
                </div>

                {/* Urgency Field */}
                <div className={styles.formField}>
                    <label className={styles.formLabel}>
                        Urgency
                    </label>
                    <div
                        onClick={() => setUrgencyDropdownOpen(!urgencyDropdownOpen)}
                        className={styles.dropdownTrigger}
                    >
                        <span>{urgency || "Select urgency"}</span>
                        {urgencyDropdownOpen ? (
                            <ChevronUp size={16} color="#6b7280" />
                        ) : (
                            <ChevronDown size={16} color="#6b7280" />
                        )}
                    </div>
                    {urgencyDropdownOpen && (
                        <div className={styles.CreateNotificationdropdownMenu}>
                            {['High', 'Medium', 'Low'].map((option) => (
                                <div
                                    key={option}
                                    className={styles.dropdownOption}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#F0FDFA";
                                        e.currentTarget.style.borderLeft = "4px solid #06C270";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "white";
                                        e.currentTarget.style.borderLeft = "4px solid transparent";
                                    }}
                                    onClick={() => {
                                        setUrgency(option);
                                        setUrgencyDropdownOpen(false);
                                    }}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Audience Field */}
                <div className={styles.formField}>
                    <label className={styles.formLabel}>
                        Audience {selectedCompanies.length > 0 && `(${selectedCompanies.length} selected)`}
                    </label>
                    <div
                        onClick={() => setAudienceDropdownOpen(!audienceDropdownOpen)}
                        className={styles.dropdownTrigger}
                    >
                        <span>{audience || "Select audience"}</span>
                        {audienceDropdownOpen ? (
                            <ChevronUp size={16} color="#6b7280" />
                        ) : (
                            <ChevronDown size={16} color="#6b7280" />
                        )}
                    </div>
                    {audienceDropdownOpen && (
                        <div className={styles.dropdownMenu}>
                            <div className={styles.searchContainer}>
                                <div className={styles.searchInputWrapper}>
                                    <input
                                        type="text"
                                        placeholder="Search company..."
                                        value={userSearch}
                                        onChange={(e) => setUserSearch(e.target.value)}
                                        className={styles.searchInput}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <Search
                                        size={16}
                                        color="#6B7280"
                                        className={styles.searchIcon}
                                    />
                                </div>
                            </div>
                            {companyList
                                .filter(option =>
                                    option.toLowerCase().includes(userSearch.toLowerCase())
                                )
                                .map((option) => (
                                    <div
                                        key={option}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCompanyToggle(option);
                                        }}
                                        className={styles.dropdownOption}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#F0FDFA";
                                            e.currentTarget.style.borderLeft = "4px solid #06C270";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "white";
                                            e.currentTarget.style.borderLeft = "4px solid transparent";
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedCompanies.includes(option)}
                                            onChange={() => handleCompanyToggle(option)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <span>{option}</span>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Add email signature Field */}
                <div className={styles.formField}>
                    <label className={styles.formLabel}>
                        Add email signature
                    </label>
                    <input
                        type="text"
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        className={styles.formInput}
                        placeholder="Enter email signature"
                    />
                </div>

                {/* Delivery method Field */}
                <div className={styles.formField}>
                    <label className={styles.formLabel}>
                        Delivery method
                    </label>
                    <div className={styles.deliveryMethods}>
                        {[
                            { key: 'popup', label: 'Popup' },
                            { key: 'banner', label: 'Banner' },
                            { key: 'email', label: 'Email' }
                        ].map(({ key, label }) => (
                            <label key={key} className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={deliveryMethods[key]}
                                    onChange={() => handleDeliveryMethodChange(key)}
                                    className={styles.checkbox}
                                    disabled={createLoading || draftLoading}
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Schedule Notification Field */}
                <div className={styles.scheduleSection}>
                    <div className={styles.deliveryMethods}>
                        <label
                            className={styles.checkboxLabel}
                            style={{ fontWeight: isScheduled ? '600' : '400', color: isScheduled ? '#111827' : '#374151' }}
                        >
                            <input
                                type="checkbox"
                                checked={isScheduled}
                                onChange={(e) => {
                                    setIsScheduled(e.target.checked);
                                    if (!e.target.checked) setScheduledDate('');
                                }}
                                className={styles.checkbox}
                                disabled={createLoading || draftLoading}
                            />
                            Schedule for later
                        </label>
                    </div>

                    {isScheduled && (
                        <div className={styles.dateInputWrapper}>
                            <Calendar
                                size={18}
                                color={scheduledDate ? "#06C270" : "#6B7280"}
                                className={styles.calendarIcon}
                            />
                            <input
                                type="datetime-local"
                                value={scheduledDate}
                                min={getMinDateTime()}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                className={styles.customDateInput}
                                placeholder="Select date and time"
                            />
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className={styles.modalFooter}>
                    <div className={styles.backButtonContainer}>
                        <button
                            onClick={handleClose}
                            className={styles.backButton}
                            disabled={createLoading || draftLoading}
                        >
                            Back
                        </button>
                    </div>

                    <div className={styles.draftButtonContainer}>
                        <button
                            className={styles.draftButton}
                            onClick={handleDraft}
                            disabled={createLoading || draftLoading}
                        >
                            {draftLoading ? "Saving..." : "Save Draft"}
                        </button>
                    </div>

                    <div className={styles.sendButtonContainer}>
                        <button
                            onClick={handleCreate}
                            className={styles.sendButton}
                            disabled={createLoading || draftLoading}
                        >
                            {createLoading
                                ? "Processing..."
                                : (isScheduled ? "Schedule" : "Send Now")
                            }
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default CreateNotificationModal;